/**
 * Rooms: several live channels watched together, with their chats in one place.
 *
 * TWO OBJECTS, because Twitch forces two (`docs/architecture/rooms-audit.md`).
 * The concurrent-join ceiling is 100 channels **per user** and reading a
 * channel you do not own needs broadcaster authorization (L42), so a channel
 * that never opted into tomo cannot be read server-side at all.
 *
 *  - A **community** is made of opted-in tomo channels. It can be discovered,
 *    read server-side, and can pay out rewards.
 *  - A **multistream** is a viewer's own saved list of Twitch logins. It is
 *    private, read browser-direct, and earns nothing.
 *
 * ADR 0041 REDREW THAT LINE. `kind` had been carrying two questions at once —
 * where the channels came from, AND whether the room is listed — so the room
 * people actually make, "a public named room of whichever streamers I like",
 * was unexpressible. Kind and visibility are now independent, and any room may
 * hold any Twitch login. The paragraph above survives as the reason
 * `room_channels` exists at all: a tomo channel can be read server-side and a
 * bare Twitch login cannot, and that is still true.
 *
 * TENANCY. A room spans channels, so a room document is NOT channel-scoped: it
 * is a platform row (L31 — `channelId` is never null, platform rows use the
 * sentinel). Only the rows that are *about one channel* carry a real
 * `channelId`, and those are immutable.
 *
 * A multistream stores Twitch LOGINS, never a `channelId`, precisely because
 * those channels have no tomo identity. `channelId` stays opaque and always
 * means a tomo channel (`ch_…`), never a Twitch name.
 */
import { z } from 'zod';
import { ChannelId } from './common.js';

export const RoomId = z.string().regex(/^rm_[a-z0-9]{8,22}$/, 'A room id looks like rm_xxxxxxxx');

/** Twitch logins: 3–25 characters, letters, digits and underscore. */
export const TwitchLogin = z
  .string()
  .regex(/^[a-z0-9_]{3,25}$/, 'A Twitch login is 3–25 letters, digits or underscores');

/**
 * What a room IS (ADR 0041). Not where its channels came from.
 *
 *  - `lobby`     exactly one, at `/live`. Everybody who has not gone elsewhere.
 *  - `community` a curated, named, owned set of channels.
 *  - `adhoc`     the channels in the URL. The link is the invite.
 *
 * `multistream` is the retired spelling of `adhoc` and is accepted on READ so
 * that rooms created under the old rules keep working. It is not creatable:
 * `RoomInput` takes `RoomKind`, which does not include it.
 */
export const RoomKind = z.enum(['lobby', 'community', 'adhoc']);
export type RoomKindValue = z.infer<typeof RoomKind>;

/** What a stored room may say, including the spelling we no longer write. */
export const StoredRoomKind = z.enum(['lobby', 'community', 'adhoc', 'multistream']);

/**
 * Whether a room is LISTED. Deliberately not whether it can be read.
 *
 * "The link is the invite": a room reached by its unguessable id is readable
 * by whoever was given the address, and `private` means it does not appear in
 * a browse list. Saying it plainly here because "private" invites the other
 * reading, and somebody will otherwise assume a private room refuses
 * strangers who have the URL. It does not. A room that must refuse them is a
 * different feature and does not exist yet.
 */
export const RoomVisibility = z.enum(['public', 'private']);
export type RoomVisibilityValue = z.infer<typeof RoomVisibility>;

/**
 * A member's standing on ONE room.
 *
 * Deliberately its own vocabulary rather than reusing `ChannelRole`. A room
 * role says who may edit the room; a channel role says what someone may do to
 * a streamer's catalog, wallet and webhooks. Sharing a type between them would
 * be the first step toward sharing a lookup table, and then owning a room
 * would mean something on a channel.
 *
 * There is no `member`: a community is public to read, so reading needs no
 * membership at all.
 */
export const ROOM_ROLES = ['owner', 'editor'] as const;
export const RoomRole = z.enum(ROOM_ROLES);
export type RoomRoleValue = z.infer<typeof RoomRole>;

/**
 * How many channels a room may list.
 *
 * Raised from 20 by ADR 0043, which separated the ROSTER from the watch set.
 * Twenty was a sensible bound on how many streams somebody watches at once and
 * a nonsensical one on how many people belong to a community — the first real
 * one has fifty-two. A room still puts only the live ones on screen.
 *
 * Raised again, to 500, for the NoPixel whitelist: over 400 streamers, all of
 * them active. Nothing that reads a roster grows with it except Twitch calls,
 * which are batched 100 at a time, so 500 is five calls where 200 was two. The
 * bound stays because a roster is one document, saved whole, and checked
 * against Twitch login by login on every save.
 */
export const ROOM_ROSTER_MAX = 500;

/**
 * A Twitch category a community is about.
 *
 * Matched by `id`: categories are renamed, and "Grand Theft Auto V" matching
 * by name would stop matching the day Twitch retitles it. The name is kept so
 * a page can show it without asking Twitch.
 */
export const RoomGame = z.object({
  id: z.string().regex(/^\d{1,20}$/, 'A Twitch category id is digits'),
  name: z.string().min(1).max(120),
  box_art_url: z.string().url().nullable().optional(),
});
export type RoomGame = z.infer<typeof RoomGame>;

/** A few, not one: Celeste, Celeste Classic and Celeste 64 are one community. */
export const ROOM_GAMES_MAX = 5;

/** `GET /v1/twitch/categories?q=`: what the game picker offers. */
export const TwitchCategoryList = z.object({ data: z.array(RoomGame) });

export const RoomSlug = z
  .string()
  .regex(/^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/, 'A slug is 3–40 lowercase letters, digits or hyphens');

export const Room = z
  .object({
    id: RoomId,
    kind: RoomKind,
    /**
     * Listed or not. Absent on a `lobby`, which is public by definition, and
     * on an `adhoc`, which is neither — it has nothing to list.
     */
    visibility: RoomVisibility.optional(),
    name: z.string().min(1).max(80),
    description: z.string().max(400).optional(),
    /** Communities only: the discoverable handle. Everything else is reached by id. */
    slug: RoomSlug.optional(),
    /**
     * How many tomo-connected channels are in it. Only those can be read
     * server-side, so this is not the size of `twitch_logins`.
     */
    channel_count: z.number().int().min(0).optional(),
    /**
     * The Twitch channels this room watches. Allowed on EVERY kind now
     * (ADR 0041) — whether a streamer has heard of tomo is not the viewer's
     * problem, and refusing logins on a community made the ordinary room
     * impossible to create.
     */
    twitch_logins: z.array(TwitchLogin).max(ROOM_ROSTER_MAX).optional(),
    /**
     * The feed this room's roster comes from, when it comes from one.
     *
     * A feed ID from a table defined in CODE (ADR 0043), never a URL. A room
     * that could name its own address to fetch would be a server-side request
     * forgery primitive, and the closed set is the whole defence.
     *
     * Deliberately absent from `RoomInput` and `RoomPatch`: attaching a feed
     * is not something a room's owner does, because it makes tomo fetch from
     * a third party on their behalf.
     */
    roster_feed: z.string().max(40).optional(),
    /**
     * The Twitch categories this community is about (ADR 0052 §6). Live
     * members playing one of them are listed before live members who are
     * not. Empty for a community about people rather than a game.
     */
    games: z.array(RoomGame).max(ROOM_GAMES_MAX).optional(),
    owner_uid: z.string().min(1),
    /**
     * What the CALLER may do here: their room role, or null.
     *
     * Present only on a single-room read, where there is one caller and one
     * room to be specific about. Absent from every list, because a listing is
     * about the rooms and answering it per row would be a role lookup per
     * room on a page that only needed names.
     *
     * A rendering hint, like `admin` on the session. Every route that acts
     * re-derives authority server-side.
     */
    your_role: RoomRole.optional(),
    /** Retired rather than deleted, like everything else that other rows point at. */
    active: z.boolean(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
  });

/**
 * What a person may ask for.
 *
 * `lobby` is absent on purpose: there is exactly one and it is created by
 * configuration, not by a request. A route that could mint a second lobby
 * would make "the lobby" a lie the first time somebody tried.
 */
export const CreatableRoomKind = z.enum(['community', 'adhoc']);

export const RoomInput = z
  .object({
    kind: CreatableRoomKind,
    visibility: RoomVisibility.optional(),
    name: z.string().min(1).max(80),
    description: z.string().max(400).optional(),
    slug: RoomSlug.optional(),
    twitch_logins: z.array(TwitchLogin).max(ROOM_ROSTER_MAX).optional(),
  })
  .refine((v) => v.kind === 'community' || v.slug === undefined, {
    message: 'Only a community has a handle; an ad-hoc room is reached by its id',
    path: ['slug'],
  })
  .refine((v) => v.kind === 'community' || v.visibility === undefined, {
    message: 'Only a community is listed or unlisted; an ad-hoc room is neither',
    path: ['visibility'],
  });

export const RoomPatch = z.object({
  name: z.string().min(1).max(80).optional(),
  description: z.string().max(400).optional(),
  visibility: RoomVisibility.optional(),
  twitch_logins: z.array(TwitchLogin).max(ROOM_ROSTER_MAX).optional(),
  /**
   * Detach from a roster feed, or point at a different one.
   *
   * NULLABLE, and the null is the whole reason this is patchable: absent
   * means "leave it alone" and null means "stop reading somebody else's
   * list". Without that distinction a community could be joined to a feed
   * and never parted from it, which is a one-way door in a product where a
   * feed is somebody else's site and can go wrong.
   *
   * Detaching does NOT copy the feed's members across. The feed's list is
   * theirs; ours is `twitch_logins`, and a caller that wants to keep who is
   * currently listed sends both in one patch. Copying silently would make a
   * community's own roster something it never chose.
   */
  roster_feed: z.string().max(40).nullable().optional(),
  /** Replaces the list whole. An empty list says the community has no game. */
  games: z.array(RoomGame).max(ROOM_GAMES_MAX).optional(),
  active: z.boolean().optional(),
});

/**
 * One channel's place in one community. The only channel-scoped row here, and
 * its `channelId` is immutable (L31).
 */
export const RoomChannel = z
  .object({
    room_id: RoomId,
    channel_id: ChannelId,
    /**
     * The Twitch login, so a client can connect to this channel's chat.
     *
     * Absent when the channel has no linked platform row, which is possible
     * and not an error — a room simply cannot show that channel's chat until
     * it is linked. The opaque `channel_id` stays the identity; this is a
     * convenience for the client, never a key.
     */
    twitch_login: TwitchLogin.optional(),
    /** Who put it there. Always someone with authority over that channel. */
    added_by_uid: z.string().min(1),
    active: z.boolean(),
    created_at: z.string().datetime(),
  });

export const RoomMember = z
  .object({
    room_id: RoomId,
    uid: z.string().min(1),
    role: RoomRole,
    created_at: z.string().datetime(),
  });

/** Inferred types, named to match their schemas — the convention everywhere here. */
export type Room = z.infer<typeof Room>;
export type CreatableRoomKindValue = z.infer<typeof CreatableRoomKind>;
export type RoomInput = z.infer<typeof RoomInput>;
export type RoomPatch = z.infer<typeof RoomPatch>;
export type RoomChannel = z.infer<typeof RoomChannel>;
export type RoomMember = z.infer<typeof RoomMember>;

/**
 * One archived message, as search returns it.
 *
 * Deliberately NOT the full `ChatMessage`: spans are a rendering concern that
 * the client rebuilds from the body and its own emote registry, and shipping
 * them would double the payload for something the reader already has.
 */
export const ArchivedMessage = z.object({
  message_id: z.string(),
  channel_id: ChannelId,
  channel_name: z.string(),
  sent_at: z.string().datetime(),
  author_login: z.string(),
  author_display_name: z.string(),
  body: z.string(),
  is_reply: z.boolean(),
  reply_parent_message_id: z.string().nullable(),
});

export const SearchResults = z.object({
  data: z.array(ArchivedMessage),
  /** True when the result was capped, so the interface can say "first N". */
  truncated: z.boolean(),
});

export type ArchivedMessage = z.infer<typeof ArchivedMessage>;
export type SearchResults = z.infer<typeof SearchResults>;

/**
 * How many people are in a room right now, and a few of them by name.
 *
 * A COUNT AND A SAMPLE, never a full list. Presence is already stored that
 * way — `PRESENCE_SAMPLE` bounds it — because reading every row per reader is
 * quadratic, and because nobody reads a thousand names. "18 here" plus four
 * faces is the same information and a better interface.
 *
 * SIGNED IN FOR ALL OF IT, count included. Occupancy is not public: how many
 * people are in a room right now is something its occupants have not
 * published, and a count being aggregate does not make it a stranger's
 * business. The stream route already requires a name to see presence, and
 * this is the same information by a different door.
 */
export const RoomPresence = z.object({
  room_id: RoomId,
  /** Everybody present, not just the ones named below. */
  total: z.number().int().nonnegative(),
  sample: z.array(z.object({
    handle: z.string(),
    avatar_url: z.string().nullable(),
  })),
});

export type RoomPresence = z.infer<typeof RoomPresence>;

/**
 * Somebody who RUNS a community, as its admin screen reads it.
 *
 * Distinct from `RoomMember`, which is the stored row: this is the assembled
 * answer, and it carries three things that row cannot. The OWNER, whose
 * standing lives on the room document rather than in a member row. The
 * HANDLE, which is what an admin recognises — nobody knows anybody by uid.
 * And a grant that has not been collected yet: a room role can be given to a
 * Twitch login that has never signed in, and the grant waits on the immutable
 * numeric id until their first login (ADR 0044 §4). A screen that could only
 * show people who already had accounts would hide exactly the invitation
 * somebody is waiting on.
 */
export const RoomAdmin = z.object({
  /** Null for a grant nobody has collected yet. */
  uid: z.string().min(1).nullable(),
  /**
   * The Twitch account a waiting grant is for; null for a member, who is
   * named by `uid`. Removal addresses one or the other and never the handle:
   * a handle is whoever holds that name NOW, which after a rename is
   * somebody else (ADR 0044 §4).
   */
  twitch_user_id: z.string().nullable(),
  /** Twitch login, for display. */
  handle: z.string().nullable(),
  role: RoomRole,
  owner: z.boolean(),
  pending: z.boolean(),
});

/** Naming somebody by the handle an admin actually knows. */
export const RoomAdminInput = z.object({
  handle: TwitchLogin,
  role: RoomRole,
});

export type RoomAdmin = z.infer<typeof RoomAdmin>;
export type RoomAdminInput = z.infer<typeof RoomAdminInput>;
